import {
  StrictMode,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";

type Theme = "light" | "dark";
const initialTheme = localStorage.getItem("griya-theme");
document.documentElement.dataset.theme =
  initialTheme === "light" || initialTheme === "dark" ? initialTheme : "dark";

function ThemeButton() {
  const [theme, setTheme] = useState<Theme>(() => {
    return document.documentElement.dataset.theme === "light"
      ? "light"
      : "dark";
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("griya-theme", theme);
  }, [theme]);

  return (
    <button
      className="theme-toggle"
      type="button"
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      aria-label={
        theme === "dark" ? "Gunakan mode terang" : "Gunakan mode gelap"
      }
    >
      <span aria-hidden="true">{theme === "dark" ? "☀" : "☾"}</span>
      {theme === "dark" ? "Mode terang" : "Mode gelap"}
    </button>
  );
}

function Root() {
  return (
    <>
      <ThemeButton />
      <App />
    </>
  );
}

type AppCode = "UTAMA" | "GUDANG" | "BENGKEL" | "PABRIK";

interface Assignment {
  app: AppCode;
  defaultRoute: string;
  permissions: string[];
}

interface SessionUser {
  id: string;
  username: string;
  displayName: string;
  role: "OWNER" | "HEAD_ADMIN" | "GUDANG_PARTMAN" | "OPERATOR";
  mustChangePassword: boolean;
  assignments: Assignment[];
}

interface SessionResponse {
  authenticated: boolean;
  user?: SessionUser;
  csrfToken?: string;
  absoluteExpiresAt?: string;
  idleExpiresAt?: string;
}

const appInfo: Record<
  Exclude<AppCode, "UTAMA">,
  { name: string; description: string; status: string }
> = {
  GUDANG: {
    name: "Gudang",
    description: "Barang masuk, barang keluar, stok, koreksi, dan riwayat.",
    status: "Data uji",
  },
  PABRIK: {
    name: "Pabrik",
    description: "Produksi, stok, penjualan, gaji borongan, dan kas pabrik.",
    status: "Aktif",
  },
  BENGKEL: {
    name: "Bengkel",
    description: "Servis, kendaraan, mekanik, suku cadang, dan kas bengkel.",
    status: "Fondasi",
  },
};

async function api<T>(
  path: string,
  options: RequestInit = {},
  csrfToken?: string,
): Promise<T> {
  const response = await fetch(`/api/utama/v1${path}`, {
    ...options,
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
      ...options.headers,
    },
  });
  const body = (await response.json()) as T & { message?: string };
  if (!response.ok) throw new Error(body.message ?? "Permintaan gagal.");
  return body;
}

function App() {
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [recoveryResult, setRecoveryResult] = useState<{
    temporaryPassword: string;
    replacementRecoveryCode: string;
  } | null>(null);

  useEffect(() => {
    void api<SessionResponse>("/auth/session")
      .then(setSession)
      .catch(() => setSession({ authenticated: false }));
  }, []);

  const assignments = useMemo(
    () =>
      session?.user?.assignments.filter((item) => item.app !== "UTAMA") ?? [],
    [session],
  );

  async function login(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      const next = await api<SessionResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify({
          username: form.get("username"),
          password: form.get("password"),
        }),
      });
      setSession(next);
      if (!next.user?.mustChangePassword && next.user?.role !== "OWNER") {
        const only = next.user?.assignments.filter(
          (item) => item.app !== "UTAMA",
        );
        if (only?.length === 1 && only[0])
          window.location.assign(only[0].defaultRoute);
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Login gagal.");
    } finally {
      setBusy(false);
    }
  }

  async function changePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session?.csrfToken) return;
    const form = new FormData(event.currentTarget);
    if (form.get("newPassword") !== form.get("confirmPassword")) {
      setError("Ulangi password baru dengan benar.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const next = await api<SessionResponse>(
        "/auth/change-password",
        {
          method: "POST",
          body: JSON.stringify({
            currentPassword: form.get("currentPassword"),
            newPassword: form.get("newPassword"),
          }),
        },
        session.csrfToken,
      );
      setSession(next);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Password gagal diganti.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    if (!session?.csrfToken) return;
    setBusy(true);
    try {
      await api(
        "/auth/logout",
        { method: "POST", body: "{}" },
        session.csrfToken,
      );
    } finally {
      setSession({ authenticated: false });
      setBusy(false);
    }
  }

  async function recoverOwner(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      const result = await api<{
        temporaryPassword: string;
        replacementRecoveryCode: string;
      }>("/auth/recover-owner", {
        method: "POST",
        body: JSON.stringify({
          username: form.get("username"),
          recoveryCode: form.get("recoveryCode"),
        }),
      });
      setRecoveryResult(result);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Pemulihan gagal.");
    } finally {
      setBusy(false);
    }
  }

  if (session === null) {
    return (
      <main className="center-shell">
        <p>Memuat halaman login…</p>
      </main>
    );
  }

  if (!session.authenticated) {
    if (recovering) {
      return (
        <main className="center-shell">
          <section className="login-card password-card">
            <p className="eyebrow">Pemulihan Owner</p>
            <h2>Pulihkan akun Fauzi</h2>
            {recoveryResult ? (
              <div className="recovery-result">
                <p>
                  Simpan dua data ini sekarang. Kode pemulihan lama sudah tidak
                  berlaku.
                </p>
                <label>
                  Password sementara
                  <output>{recoveryResult.temporaryPassword}</output>
                </label>
                <label>
                  Kode pemulihan baru
                  <output>{recoveryResult.replacementRecoveryCode}</output>
                </label>
                <button
                  onClick={() => {
                    setRecoveryResult(null);
                    setRecovering(false);
                  }}
                >
                  Kembali ke login
                </button>
              </div>
            ) : (
              <form onSubmit={recoverOwner} autoComplete="off">
                <label>
                  Username Owner
                  <input name="username" required autoFocus />
                </label>
                <label>
                  Kode pemulihan offline
                  <input
                    name="recoveryCode"
                    type="password"
                    required
                    autoComplete="off"
                  />
                </label>
                {error && (
                  <p className="error" role="alert">
                    {error}
                  </p>
                )}
                <button disabled={busy}>
                  {busy ? "Memulihkan…" : "Buat password sementara"}
                </button>
                <button
                  className="secondary"
                  type="button"
                  onClick={() => {
                    setError("");
                    setRecovering(false);
                  }}
                >
                  Batal
                </button>
              </form>
            )}
          </section>
        </main>
      );
    }
    return (
      <main className="login-shell">
        <section className="brand-panel">
          <p className="eyebrow">Sistem operasional</p>
          <h1>Griya Adikara</h1>
          <p className="lead">
            Masuk sekali untuk membuka aplikasi sesuai tugas Anda.
          </p>
          <div className="trust-note">
            Akun pribadi · Aktivitas tercatat · Akses dibatasi
          </div>
        </section>
        <section className="login-card" aria-label="Login Griya Adikara">
          <p className="eyebrow">Masuk</p>
          <h2>Selamat datang</h2>
          <form onSubmit={login} autoComplete="off">
            <label>
              Username
              <input
                name="username"
                autoComplete="username"
                required
                autoFocus
              />
            </label>
            <label>
              Password
              <input
                name="password"
                type="password"
                autoComplete="off"
                required
              />
            </label>
            {error && (
              <p className="error" role="alert">
                {error}
              </p>
            )}
            <button disabled={busy} type="submit">
              {busy ? "Memeriksa…" : "Masuk"}
            </button>
            <button
              className="secondary"
              type="button"
              onClick={() => {
                setError("");
                setRecovering(true);
              }}
            >
              Pulihkan akun Owner
            </button>
          </form>
        </section>
      </main>
    );
  }

  if (session.user?.mustChangePassword) {
    return (
      <main className="center-shell">
        <section className="login-card password-card">
          <p className="eyebrow">Keamanan akun</p>
          <h2>Buat password baru</h2>
          <p>Password awal wajib diganti sebelum membuka aplikasi.</p>
          <form onSubmit={changePassword}>
            <label>
              Password sementara
              <input name="currentPassword" type="password" required />
            </label>
            <label>
              Password baru
              <input
                name="newPassword"
                type="password"
                minLength={8}
                required
              />
            </label>
            <label>
              Ulangi password baru
              <input
                name="confirmPassword"
                type="password"
                minLength={8}
                required
              />
            </label>
            <p className="hint">
              Minimal 8 karakter, dengan huruf besar, huruf kecil, dan angka.
            </p>
            {error && (
              <p className="error" role="alert">
                {error}
              </p>
            )}
            <button disabled={busy} type="submit">
              Simpan password
            </button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="portal-shell">
      <header className="portal-header">
        <div>
          <p className="eyebrow">Portal usaha</p>
          <h1>Griya Adikara</h1>
          <p className="lead">Pilih aplikasi yang akan dibuka.</p>
        </div>
        <div className="user-box">
          <span>{session.user?.displayName}</span>
          <small>{session.user?.role === "OWNER" ? "Owner" : "Pengguna"}</small>
          <button
            className="secondary"
            disabled={busy}
            onClick={() => void logout()}
          >
            Keluar
          </button>
        </div>
      </header>

      <section aria-label="Aplikasi" className="unit-grid">
        {assignments.map((assignment) => {
          const info = appInfo[assignment.app as Exclude<AppCode, "UTAMA">];
          if (!info) return null;
          return (
            <a
              className="unit-card"
              href={assignment.defaultRoute}
              key={assignment.app}
            >
              <span
                className={`status ${assignment.app === "GUDANG" ? "pilot" : ""}`}
              >
                {info.status}
              </span>
              <h2>{info.name}</h2>
              <p>{info.description}</p>
              <span className="action">Buka aplikasi →</span>
            </a>
          );
        })}
      </section>
      <footer>Portal utama tidak menyimpan transaksi operasional unit.</footer>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
